import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { EntryKey } from './access';

/**
 * Gepostete Nachrichten — what the bot posted from the dashboard (web-api
 * routes/posted.ts). Rights follow the editor of each type.
 */

export type PostedType = 'rules' | 'support-rules' | 'tickets' | 'self-roles' | 'games' | 'nsfw' | 'embed' | 'message';

export interface PostedMessage {
  id: string;
  guildId: string;
  channelId: string;
  messageId: string;
  type: PostedType;
  data: Record<string, unknown>;
  postedBy: string | null;
  adopted: boolean;
  createdAt: string;
  updatedAt: string;
}

export const POSTED_LABEL: Record<PostedType, string> = {
  rules: 'Regeln-Panel',
  'support-rules': 'Support-Regeln-Panel',
  tickets: 'Ticket-Panel',
  'self-roles': 'Self-Roles-Panel',
  games: 'Games-Panel',
  nsfw: 'NSFW-Post',
  embed: 'Embed',
  message: 'Nachricht',
};

export const POSTED_ENTRY: Record<PostedType, EntryKey> = {
  rules: 'rules',
  'support-rules': 'supportRules',
  tickets: 'tickets',
  'self-roles': 'selfRoles',
  games: 'games',
  nsfw: 'nsfw',
  embed: 'embed',
  message: 'actions',
};

/** Where "Bearbeiten" leads (relative to /server/:guildId); null = only delete. */
export function editPath(p: PostedMessage): string | null {
  switch (p.type) {
    case 'rules':
      return '/config/panels/rules';
    case 'support-rules':
      return '/config/panels/support-rules';
    case 'tickets':
      return '/config/tickets';
    case 'self-roles':
      return '/config/panels/self-roles';
    case 'games':
      return '/config/games-catalog';
    case 'embed':
    case 'message':
      return `/embed?edit=${p.id}`;
    default:
      return null;
  }
}

/** One line of what's in the message, for lists. */
export function postedSummary(p: PostedMessage): string {
  const d = p.data as Record<string, unknown>;
  const embeds = (d.embeds as Array<{ title?: string; description?: string }> | undefined) ?? [];
  const text =
    (d.panelTitle as string) ||
    (d.gameName as string) ||
    (d.content as string) ||
    embeds[0]?.title ||
    embeds[0]?.description ||
    (d.nsfwType as string) ||
    '';
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

export const postedKey = (guildId: string) => ['posted', guildId];

export function usePosted(guildId: string, type?: PostedType) {
  return useQuery({
    queryKey: [...postedKey(guildId), type ?? 'all'],
    queryFn: () =>
      api<{ posted: PostedMessage[] }>(`/api/guilds/${guildId}/posted${type ? `?type=${type}` : ''}`),
  });
}

/** Posting goes through the bot (async) — refresh the lists a moment later. */
export function refreshPostedSoon(qc: QueryClient, guildId: string) {
  for (const ms of [1500, 4000]) {
    setTimeout(() => qc.invalidateQueries({ queryKey: postedKey(guildId) }), ms);
  }
}
